// A planned route retires by `cancelled` status or a past `effective_to` (still `live`, but no longer
// generated). Dates compare as `YYYY-MM-DD` strings so a date-only column doesn't shift by timezone.
import { format } from "date-fns"

import { RoutePlannedStatus } from "@/types/collections"

export interface RetirableRoute {
  status: RoutePlannedStatus
  effective_to?: string | null
}

export type PlannedRouteLifecycle = "draft" | "live" | "retired"

/** `cancelled` outranks `expired` when both apply. */
export interface RetirementReason {
  kind: "cancelled" | "expired"
  /** Date-only. Absent for a cancellation. */
  endedOn?: string
}

export function todayDateOnly(now: Date = new Date()): string {
  return format(now, "yyyy-MM-dd")
}

// `effective_to` arrives as a full ISO timestamp from some paths and date-only from others.
function dateOnly(value: string): string {
  return value.split("T")[0]
}

export function retirementReason(
  route: RetirableRoute,
  today: string = todayDateOnly(),
): RetirementReason | null {
  if (route.status === "cancelled") return { kind: "cancelled" }

  if (route.status === "draft") return null

  if (!route.effective_to) return null

  const endedOn = dateOnly(route.effective_to)
  // An end date of today still runs today.
  if (endedOn >= today) return null

  return { kind: "expired", endedOn }
}

export function isRetiredPlannedRoute(
  route: RetirableRoute,
  today: string = todayDateOnly(),
): boolean {
  return retirementReason(route, today) !== null
}

export function plannedRouteLifecycle(
  route: RetirableRoute,
  today: string = todayDateOnly(),
): PlannedRouteLifecycle {
  if (isRetiredPlannedRoute(route, today)) return "retired"
  return route.status === "draft" ? "draft" : "live"
}

/** Most recently expired first; cancellations (no date) last. */
export function retiredPlannedRoutes<T extends RetirableRoute>(
  routes: T[],
  today: string = todayDateOnly(),
): T[] {
  return routes
    .filter((route) => isRetiredPlannedRoute(route, today))
    .sort((a, b) => {
      const aEnd = retirementReason(a, today)?.endedOn
      const bEnd = retirementReason(b, today)?.endedOn
      if (aEnd && bEnd) return bEnd.localeCompare(aEnd)
      if (aEnd) return -1
      if (bEnd) return 1
      return 0
    })
}
